import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'

import { useQuery } from '@tanstack/react-query'

import imagePlaceholder from '~/assets/img/Image_placeholder.png'
import { getAccountId } from '~/helpers'
import { capitalizeWords } from '~/helpers/capitalize-words'
import * as types from '~/modules/retailMedia/store/product/constants'
import { setQuery } from '~/store/modules/filters/actions'

import {
  getPublishersOnsite,
  getPublishersOnsiteKey
} from '../../../../services/publisher'
import { deleteAllProductsOnCampaign } from '../../../../store/campaign/actions'
import { useCampaignForm } from '../../hooks/useCampaignForm'

export function useDestinationRetailer() {
  const [searchValue, setSearchValue] = useState('')
  const dispatch = useDispatch()
  const accountId = getAccountId()
  const destinationEligibleParams = { destination_eligible: true as const }

  const { setAllSubpublishers, setSelectedSubpublishers } = useCampaignForm()

  const { data: publishers, isPending } = useQuery({
    queryKey: [getPublishersOnsiteKey, accountId, destinationEligibleParams],
    queryFn: async () => {
      const firstPage = await getPublishersOnsite(destinationEligibleParams)
      const totalPages = firstPage?.data?.pages

      const promises = []
      if (!!totalPages && totalPages > 1) {
        for (let i = 2; i <= totalPages; i++) {
          promises.push(
            getPublishersOnsite({ ...destinationEligibleParams, page: i })
          )
        }
      }
      const results = await Promise.all(promises)

      const allData = [
        ...(firstPage?.data?.data ?? []),
        ...results.flatMap(r => r?.data?.data ?? [])
      ]

      return allData.map(item => {
        const iconSrc = item?.publisher_account_logo
          ? item?.publisher_account_logo
          : imagePlaceholder

        return {
          id: item?.publisher_id,
          value: item?.publisher_id,
          label: capitalizeWords(item?.publisher_name),
          icon: (
            <img
              src={iconSrc}
              height={20}
              width={20}
              style={{ objectFit: 'contain' }}
            />
          )
        }
      })
    }
  })

  const publishersFiltered = useMemo(() => {
    if (!searchValue) {
      return publishers
    }

    return (
      publishers?.filter(item =>
        item?.label?.toLowerCase()?.includes(searchValue?.toLowerCase())
      ) || []
    )
  }, [searchValue, publishers])

  const resetFieldQueryAndDeleteAllProducts = useCallback(() => {
    dispatch(
      setQuery({
        query: { [types.PRODUCT_REDUX_QUERY]: null },
        keyState: types.PRODUCT_REDUX_QUERY
      })
    )
    dispatch(deleteAllProductsOnCampaign())
  }, [dispatch])

  const handleChoosePublishers = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const publisherId = e?.currentTarget?.value as PublisherId
      setSelectedSubpublishers([publisherId])
      resetFieldQueryAndDeleteAllProducts()
    },
    [setSelectedSubpublishers, resetFieldQueryAndDeleteAllProducts]
  )

  useEffect(() => {
    if (publishers) {
      setAllSubpublishers(
        publishers.map(item => ({
          id: item?.id,
          name: item?.label
        }))
      )
    }
  }, [publishers, setAllSubpublishers])

  return {
    searchValue,
    setSearchValue,
    isPending,
    publishersFiltered,
    handleChoosePublishers
  }
}
